import React from 'react'
import { Box, useTheme, Typography, Chip } from "@mui/material";
import { tokens } from "../../../theme";
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Divider from '@mui/material/Divider';
import MoreVertIcon from '../../../components/MoreVertIcon';
import Popover from '@mui/material/Popover';
import { styled } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import DeleteDialog from '../../../components/DeleteDialog';
import Axios from 'axios';
import UpdateModal from './UpdateModal';
import getStudentName from '../../../utils/getStudentName';
import { getAuthHeader } from '../../../utils/authToken';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import PublishIcon from '@mui/icons-material/Publish';
import UnpublishedIcon from '@mui/icons-material/Unpublished';

var options = {
    year: "numeric",
    month: "short",
    day: "numeric"
};

// an evaluation pasted as one unbroken string has no place to wrap, so the cell grows to
// fit it and the whole table scrolls sideways. `anywhere` lets the browser break inside
// such a run — and unlike `break-all` it only does so when the word cannot fit, leaving
// ordinary prose to wrap on spaces as usual
const wrapLongText = { wordBreak: 'break-word', overflowWrap: 'anywhere' };

// each evaluation is stacked as a label row above its content row rather than sharing the
// width with the other one, so a long evaluation has the whole table to wrap in
const sectionLabelSx = {
    py: 0.75,
    fontWeight: 'bold',
    fontSize: 12,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    opacity: 0.75,
    // a neutral overlay rather than a palette token: it darkens the light theme's near-white
    // row and lightens the dark theme's navy one, so the label reads as a divider in both
    backgroundColor: 'rgba(128, 128, 128, 0.12)',
};

const LogHistory = ({
    eventModule,
    courseStudents,
    courseId,
    userRole,
    index,
    setBackdropOpen,
    setSuccessAlertOpen,
    setErrorAlertOpen,
    setSuccessAlertMessage,
    setErrorAlertMessage,
 }) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const StyledTableCell = styled(TableCell)(({ theme }) => ({
        [`&.${tableCellClasses.head}`]: {
            backgroundColor: colors.primary[300],
            color: theme.palette.common.white,
        },
        [`&.${tableCellClasses.body}`]: {
            fontSize: 14,
        },
        border: '1px solid rgba(224, 224, 224, 1)',
    }));
      
    const StyledTableRow = styled(TableRow)(({ theme }) => ({
        '&:nth-of-type(odd)': {
            backgroundColor: colors.primary[750],
        },
        '&:nth-of-type(even)': {
            backgroundColor: colors.primary[750],
        },
    }));
    
    // for moreVertIcon popover
    const [anchorEl, setAnchorEl] = React.useState(null);
    const handleMoreVertIconClick = (event) => {
        if (userRole === "student")
            return;
        setAnchorEl(event.currentTarget);
    };
    const handlePopOverClose = () => {
        setAnchorEl(null);
    };
    const popoverOpen = Boolean(anchorEl);
    const popoverId = popoverOpen ? 'popover' : undefined;

    const studentIdToName = {};
    if (courseStudents !== null) {
        // create a hash map of student id to student name
        if (courseStudents !== null) {
            courseStudents.forEach((element) => {
                const student_name = getStudentName(element.student);
                studentIdToName[element.student.id] = student_name;
            });
        }
    }

    // for deleting log
    const handleDeleteLog = async () => {
        setBackdropOpen(true);
        await Axios.post(`${process.env.REACT_APP_URL}/api/v1/events/${eventModule.id}/special-notes/deletion`, {
            all_special_notes: eventModule.special_notes
        }, {
            headers: {
                Authorization: getAuthHeader(),
            },
        }).then((res) => {
            setSuccessAlertMessage("Log deleted successfully!");
            setSuccessAlertOpen(true);
            setBackdropOpen(false);
            window.location.reload();
        })
        .catch((err) => {
            setErrorAlertMessage(err.response?.data?.message || "Something went wrong");
            setErrorAlertOpen(true);
            setBackdropOpen(false);
        });
    };

    return (
        <Box sx={{ mb: "1rem" }}>
            <Accordion 
                key={eventModule.id}
                sx={{ 
                    backgroundColor: colors.primary[750],
                    boxShadow: "0px 0px 1px 2px rgba(0, 0, 0, 0.30)",
                }}
                defaultExpanded={index === 0 ? true : false}
            >
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="panel1a-content"
                    id="panel1a-header"
                >
                    <Typography>{eventModule.title}</Typography>
                    <Typography sx={{ ml: "auto", mr: "1rem" }}>
                        {new Date(eventModule.start).toLocaleDateString("en", options)}
                    </Typography>
                </AccordionSummary>
                <Divider />
                <AccordionDetails sx={{ backgroundColor: colors.primary[600] }} >
                    <Box sx={{ display: "flex", justifyContent: "space-between", py: "1rem"}}>
                        <Box sx={{ display: "flex", flexDirection: "column", py: "1rem"}}>
                            <Typography fontWeight="bold">Progress:</Typography>
                            {[
                                { chapter: eventModule.progress, from: eventModule.progress_from, to: eventModule.progress_to, test: eventModule.test_slot_1 },
                                { chapter: eventModule.progress_2, from: eventModule.progress_from_2, to: eventModule.progress_to_2, test: eventModule.test_slot_2 },
                                { chapter: eventModule.progress_3, from: eventModule.progress_from_3, to: eventModule.progress_to_3, test: eventModule.test_slot_3 },
                            ]
                                .filter((row) => row.chapter)
                                .map((row, i) => (
                                    <Typography key={i} sx={{ ml: "1.5rem" }}>
                                        {row.chapter} (pp.{row.from} - pp.{row.to}) — Test: {row.test?.title || "N/A"}
                                    </Typography>
                                ))}
                        </Box>
                        <MoreVertIcon handleClick={handleMoreVertIconClick} />
                        <Popover
                            id={popoverId}
                            open={popoverOpen}
                            anchorEl={anchorEl}
                            onClose={handlePopOverClose}
                            anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'left',
                            }}
                        >
                            <UpdateModal
                                eventModule={eventModule}
                                special_notes={eventModule.special_notes}
                                courseStudents={courseStudents}
                                courseId={courseId}
                                setAnchorEl={setAnchorEl}
                                setBackdropOpen={setBackdropOpen}
                                setSuccessAlertOpen={setSuccessAlertOpen}
                                setErrorAlertOpen={setErrorAlertOpen}
                                setSuccessAlertMessage={setSuccessAlertMessage}
                                setErrorAlertMessage={setErrorAlertMessage}
                                userRole={userRole}
                            />
                            {userRole === "admin" &&
                                <DeleteDialog 
                                    handleDelete={handleDeleteLog}
                                    setAnchorEl={setAnchorEl} 
                                    dialogTitle={"Are you sure you want to delete this log?"} 
                                />
                            }
                        </Popover>
                    </Box>
                    <Divider />
                    <TableContainer component={Paper}>
                        {/* fixed layout keeps the declared column widths no matter how long a
                            single evaluation is, so the table never grows past its container */}
                        <Table aria-label="simple table" sx={{ tableLayout: 'fixed', width: '100%' }}>
                            <TableHead>
                                {/* three columns only. the AI evaluation is long-form text and gets
                                    a full-width row of its own beneath each student, rather than a
                                    column that squeezes the teacher's evaluations and pushes the
                                    publish status off the side of the table */}
                                <TableRow>
                                    <StyledTableCell sx={{ width: '18%' }}>Name</StyledTableCell>
                                    <StyledTableCell sx={{ width: '82%' }}>Evaluation</StyledTableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {eventModule.special_notes !== null && eventModule.special_notes.length > 0 &&
                                    eventModule.special_notes.map((special_note) => {
                                        const hasAiEval = special_note.ai_eval || special_note.ai_eval_en;

                                        return (
                                            <React.Fragment key={special_note.student_id}>
                                                <StyledTableRow>
                                                    {/* the name spans the whole student block — five rows for an
                                                        admin, four for a teacher, who gets no AI evaluation row */}
                                                    <StyledTableCell
                                                        component="th"
                                                        scope="special_note"
                                                        rowSpan={userRole === "admin" ? 5 : 4}
                                                        sx={{ verticalAlign: 'top', ...wrapLongText }}
                                                    >
                                                        {studentIdToName[special_note.student_id]}
                                                    </StyledTableCell>
                                                    <StyledTableCell sx={sectionLabelSx}>Evaluation 1</StyledTableCell>
                                                </StyledTableRow>
                                                <StyledTableRow>
                                                    <StyledTableCell sx={{ verticalAlign: 'top', ...wrapLongText }}>
                                                        {special_note.eval_1 && special_note.eval_1.split("\n").map((item, key) => {
                                                            return <span key={key}>{item}<br/></span>
                                                        })}
                                                    </StyledTableCell>
                                                </StyledTableRow>
                                                <StyledTableRow>
                                                    <StyledTableCell sx={sectionLabelSx}>Evaluation 2</StyledTableCell>
                                                </StyledTableRow>
                                                <StyledTableRow>
                                                    <StyledTableCell sx={{ verticalAlign: 'top', ...wrapLongText }}>
                                                        {special_note.eval_2 && special_note.eval_2.split("\n").map((item, key) => {
                                                            return <span key={key}>{item}<br/></span>
                                                        })}
                                                    </StyledTableCell>
                                                </StyledTableRow>
                                                {userRole === "admin" && (
                                                    <StyledTableRow>
                                                        <StyledTableCell sx={wrapLongText}>
                                                            {/* the status describes the AI evaluation, not the student,
                                                                so it belongs on this block rather than in a column */}
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                                                                <AutoAwesomeIcon sx={{ fontSize: 16 }} color="info" />
                                                                <Typography variant="caption" fontWeight="bold" sx={{ letterSpacing: '0.04em' }}>
                                                                    AI EVALUATION (KR / EN)
                                                                </Typography>
                                                                <Box sx={{ flexGrow: 1 }} />
                                                                {hasAiEval && (
                                                                    <Chip
                                                                        size="small"
                                                                        label={special_note.ai_eval_published ? "Published" : "Draft"}
                                                                        color={special_note.ai_eval_published ? "success" : "default"}
                                                                        icon={special_note.ai_eval_published ? <PublishIcon /> : <UnpublishedIcon />}
                                                                    />
                                                                )}
                                                            </Box>
                                                            {hasAiEval ? (
                                                                <>
                                                                    <Box sx={{ mb: 1 }}>
                                                                        <Typography variant="caption" color="primary" fontWeight="bold">KR:</Typography>
                                                                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.5, ...wrapLongText }}>
                                                                            {special_note.ai_eval || <em style={{ color: '#999' }}>Not generated</em>}
                                                                        </Typography>
                                                                    </Box>
                                                                    <Box>
                                                                        <Typography variant="caption" color="primary" fontWeight="bold">EN:</Typography>
                                                                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.5, ...wrapLongText }}>
                                                                            {special_note.ai_eval_en || <em style={{ color: '#999' }}>Not generated</em>}
                                                                        </Typography>
                                                                    </Box>
                                                                </>
                                                            ) : (
                                                                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                                                    No AI evaluation
                                                                </Typography>
                                                            )}
                                                        </StyledTableCell>
                                                    </StyledTableRow>
                                                )}
                                            </React.Fragment>
                                        );
                                    })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    {/* <Divider /> */}
                </AccordionDetails>
            </Accordion>
        </Box>
    )
}

export default LogHistory